/** @format */

import { CLOSE_LOADER, LOADER_OPEN } from "../Component/store/dialogue/dialogue.type";
import { baseURL, secretKey } from "./config";
import store from "../Component/store/Provider";
import { setToast } from "./toast";
import axios from "axios";

export const openSpinner = () => {
	return store.dispatch({ type: LOADER_OPEN });
};

export const closeSpinner = () => {
	return store.dispatch({ type: CLOSE_LOADER });
};

// Guard so we only show the session-expired toast / redirect once per
// burst of 401s (e.g. when several requests are in flight at the same time).
let isHandlingSessionExpired = false;

// Routes that already handle unauthenticated state themselves — we must NOT
// pop the "Session expired" toast or redirect again when a 401 happens here,
// otherwise users get bounced mid-login / mid-password-reset.
const UNAUTH_PATHS = ["/admin-login", "/register", "/forgotPassword", "/changePassword"];

function isOnAuthPage() {
	if (typeof window === "undefined") return false;
	const path = window.location.pathname || "";
	return UNAUTH_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

function hadActiveSession() {
	try {
		const t = sessionStorage.getItem("token");
		return !!(t && t !== "null" && t !== "undefined");
	} catch (_e) {
		return false;
	}
}

function handleSessionExpired() {
	if (isHandlingSessionExpired) return;

	// If we're already on a public/auth page, the 401 came from a background
	// check (e.g. AuthRoute's /login probe) — just ignore it.
	if (isOnAuthPage()) return;

	const hadSession = hadActiveSession();
	isHandlingSessionExpired = true;

	try {
		sessionStorage.clear();
		localStorage.clear();
		if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
			axios.defaults.headers.common["key"] = "";
			axios.defaults.headers.common["Authorization"] = "";
		}
	} catch (e) {
		// ignore storage errors
	}

	// Only show the "Session expired" toast if the user actually had a token.
	// If they were never logged in, silently redirect — no confusing toast.
	if (hadSession) {
		setToast("error", "Session expired. Please log in again.");
	}

	// Give the toast a moment to render before redirecting to the login page.
	setTimeout(
		() => {
			if (typeof window !== "undefined" && window.location.pathname !== "/admin-login") {
				window.location.href = "/admin-login";
			}
		},
		hadSession ? 1200 : 0,
	);
}

function handleErrors(response) {
	if (response && response.status === 401) {
		handleSessionExpired();
		throw new Error("Session expired. Please log in again.");
	}
	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response?.status}`);
	}
	return response.json();
}

export { handleSessionExpired };
export const apiInstanceFetch = {
	baseURL: `${baseURL}`, // Set your default base URL here
	headers: {
		"Content-Type": "application/json",
		key: `${secretKey}`,
	},
	get: async (url) => {
		openSpinner();
		return fetch(`${apiInstanceFetch?.baseURL}${url}`, {
			method: "GET",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	post: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: data,
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	patch: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "PATCH",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: JSON.stringify(data),
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	put: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "PUT",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: JSON.stringify(data),
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	delete: (url) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "DELETE",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},
};
